/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useRef, useMemo } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Card } from 'sharedUi/Card';
import { Input } from 'sharedUi/Input';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import PageHeader from '../../components/common/page-header';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { extractErrorMessage } from '../../utils/error-handler';
import {
  emailService,
  uploadAttachment,
  type EmailAttachmentIn,
} from '../../services/email.service';

/** Split a comma/semicolon/whitespace separated address list. */
const parseAddresses = (raw: string): string[] =>
  raw
    .split(/[,;\s]+/)
    .map((a) => a.trim())
    .filter(Boolean);

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/bmp'];

export default function EmailCompose() {
  const navigate = useAppNavigate();
  const toast = useRef<any>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('<p>Hello,</p>\n<p></p>');
  const [attachments, setAttachments] = useState<EmailAttachmentIn[]>([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Regenerated per compose session so a double-click can't send twice, while a
  // genuine second message still gets its own key.
  const idempotencyKey = useMemo(
    () => `compose-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    [],
  );

  const canSend = parseAddresses(to).length > 0 && subject.trim().length > 0;

  /**
   * Upload each file and keep only the returned reference.
   *
   * Previously these were base64-encoded in the browser and the encoded bytes
   * were stored inside the email_outbox document. MongoDB caps a document at
   * 16MB, so anything over ~11MB of real file passed validation and then failed
   * the insert. Uploading first keeps the queued row tiny and lifts the limit to
   * whatever the mail provider allows.
   */
  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const next: EmailAttachmentIn[] = [];
      for (const file of Array.from(files)) {
        const r = await uploadAttachment(file);
        next.push({
          fileId: r.fileId,
          filename: r.filename,
          mimeType: r.mimeType,
          sizeBytes: r.sizeBytes,
          isInline: false,
        });
      }
      setAttachments((prev) => [...prev, ...next]);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Upload failed',
        detail: extractErrorMessage(error),
      });
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  /** Flip an image between a normal attachment and an inline cid: image. */
  const toggleInline = (index: number) => {
    setAttachments((prev) =>
      prev.map((a, i) => {
        if (i !== index) return a;
        const nowInline = !a.isInline;
        const contentId =
          a.contentId || (a.filename || `img${i + 1}`).replace(/\W+/g, '').toLowerCase();
        return { ...a, isInline: nowInline, contentId: nowInline ? contentId : undefined };
      }),
    );
  };

  const insertCidTag = (att: EmailAttachmentIn) => {
    if (!att.contentId) return;
    setBodyHtml((prev) => `${prev}\n<img src="cid:${att.contentId}" alt="${att.filename || ''}" />`);
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const result = await emailService.preview({ subject, bodyHtml, wrapInShell: true });
      setPreviewHtml(result.html);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Preview failed',
        detail: extractErrorMessage(error),
      });
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const row = await emailService.send(
        {
          to: parseAddresses(to),
          cc: parseAddresses(cc),
          bcc: parseAddresses(bcc),
          subject,
          bodyHtml,
          attachments,
        },
        idempotencyKey,
      );
      toast.current?.show({
        severity: 'success',
        summary: 'Queued',
        detail: 'The message was accepted and will be delivered shortly.',
      });
      navigate(`/email/outbox/${row.id}`);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Could not queue email',
        detail: extractErrorMessage(error),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <PermissionGuard permission="NOTIFICATION_MASTER.UPDATE" resourceName="Compose Email">
      <Toast ref={toast} />
      <PageHeader
        icon="pi pi-send"
        title="Compose Email"
        subtitle="HTML body, attachments and inline images"
        actions={
          <div className="flex items-center gap-2">
            <Button
              label="Cancel"
              text
              onClick={() => navigate('/email/outbox')}
            />
            <Button
              label="Preview"
              icon="pi pi-eye"
              severity="secondary"
              disabled={previewing}
              onClick={() => void handlePreview()}
            />
            <Button
              label="Send"
              icon="pi pi-send"
              disabled={!canSend || sending}
              onClick={() => void handleSend()}
            />
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">To</label>
              <Input
                value={to}
                onChange={setTo}
                placeholder="priya@example.com, ravi@example.com"
                className="w-full"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Cc</label>
                <Input value={cc} onChange={setCc} className="w-full" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">
                  Bcc <span className="normal-case text-slate-400">(hidden)</span>
                </label>
                <Input value={bcc} onChange={setBcc} className="w-full" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">Subject</label>
              <Input
                value={subject}
                onChange={setSubject}
                className="w-full"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">
                HTML body
              </label>
              <textarea
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
                rows={14}
                spellCheck={false}
                className="w-full rounded border border-slate-300 p-2 font-mono text-sm"
              />
              <p className="mt-1 text-xs text-slate-500">
                Scripts and event handlers are stripped before sending.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase tracking-wide text-slate-500">
                  Attachments
                </label>
                <Button
                  label={uploading ? 'Uploading…' : 'Add files'}
                  icon="pi pi-paperclip"
                  disabled={uploading}
                  text
                  onClick={() => fileInput.current?.click()}
                />
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => void handleFiles(e.target.files)}
                />
              </div>

              {attachments.length === 0 && (
                <p className="text-xs text-slate-400">No attachments</p>
              )}

              <ul className="space-y-1">
                {attachments.map((a, i) => {
                  const isImage = IMAGE_TYPES.includes(a.mimeType || '');
                  return (
                    <li
                      key={`${a.filename}-${i}`}
                      className="flex items-center gap-2 rounded border border-slate-200 p-2 text-sm"
                    >
                      <i className={a.isInline ? 'pi pi-image' : 'pi pi-paperclip'} />
                      <span className="flex-1 truncate">{a.filename}</span>
                      {isImage && (
                        <Button
                          label={a.isInline ? 'Inline' : 'Make inline'}
                          text
                          onClick={() => toggleInline(i)}
                        />
                      )}
                      {a.isInline && a.contentId && (
                        <Button
                          label={`cid:${a.contentId}`}
                          text
                          onClick={() => insertCidTag(a)}
                        />
                      )}
                      <Button
                        icon="pi pi-trash"
                        text
                        severity="danger"
                        aria-label="Remove"
                        onClick={() =>
                          setAttachments((prev) => prev.filter((_, index) => index !== i))
                        }
                      />
                    </li>
                  );
                })}
              </ul>
              <p className="mt-1 text-xs text-slate-500">
                Inline images embed in the body via <code>cid:</code> — they render
                without the recipient having to allow remote images. Total size limit
                is 25&nbsp;MB.
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="mb-2 text-xs uppercase tracking-wide text-slate-500">Preview</div>
          {previewHtml ? (
            // Sandboxed: the server sanitizes, but the preview is still
            // template-authored markup — don't give it access to this origin.
            <iframe
              title="Email preview"
              sandbox=""
              srcDoc={previewHtml}
              className="h-[640px] w-full rounded border border-slate-200 bg-white"
            />
          ) : (
            <div className="flex h-[640px] items-center justify-center rounded border border-dashed border-slate-300 text-sm text-slate-400">
              Click Preview to render the message
            </div>
          )}
          <p className="mt-2 text-xs text-slate-500">
            Inline <code>cid:</code> images will not appear here — they only resolve
            in a real mail client.
          </p>
        </Card>
      </div>
    </PermissionGuard>
  );
}
